import React, { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  ChevronRight,
  FileSpreadsheet,
  Filter,
  RefreshCw,
  Search,
} from 'lucide-react';
import { fetchAttendanceRowsForRangeFast, toDate } from '../services/attendanceDb';
import {
  readHrMonthlyAttendanceCache,
  writeHrMonthlyAttendanceCache,
} from '../utils/appSessionCache';

const COLORS = ['#3b5bdb', '#6d28d9', '#2a9d4f', '#d97706', '#c0392b', '#1e7ec8', '#6b4f9e'];
const CBGS = ['#eef2ff', '#f5f3ff', '#edfaf2', '#fffbeb', '#fef2f2', '#e6f3fc', '#f0edfb'];
const PRESENT_STATUSES = new Set(['IN', 'OUT', 'AUTO_CHECKOUT']);
const FULL_DAY_HOURS = 9;
const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function ec(i) { return COLORS[i % COLORS.length]; }
function ecb(i) { return CBGS[i % CBGS.length]; }

const parseDateKey = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = (dateStr) => {
  const base = parseDateKey(dateStr) || new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1, 12, 0, 0, 0);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0, 12, 0, 0, 0);
  return {
    monthStart: formatDateKey(start),
    monthEnd: formatDateKey(end),
    monthLabel: start.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
  };
};

const getMonthValue = (dateStr) => {
  const date = parseDateKey(dateStr);
  return date ? date.getFullYear() * 12 + date.getMonth() : 0;
};

const getMonthDateKeys = (startStr, endStr) => {
  const start = parseDateKey(startStr);
  const end = parseDateKey(endStr);
  if (!start || !end || start > end) return [];

  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(formatDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const isHalfDayRecord = (record) => {
  if (!record) return false;
  if (record.dayType === 'HALF_DAY') return true;
  if (!record.totalHours || record.totalHours <= 0 || record.totalHours >= FULL_DAY_HOURS) return false;
  return !!(record.checkOut || record.status === 'OUT' || record.status === 'AUTO_CHECKOUT');
};

const formatDuration = (hours) => {
  if (!hours || hours <= 0) return '--';
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDayLabel = (dateStr) => {
  const date = parseDateKey(dateStr);
  return date
    ? date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
    : dateStr;
};

const formatTimeValue = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--';
};

const normalizeLeaveStatus = (status) => {
  if (status === 'pending_head') return 'Pending (head)';
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const isActiveLeaveStatus = (status) => status && status !== 'rejected';

const getLeaveDayFraction = (leave, dateStr) => {
  if (!leave?.from || !leave?.to) return 0;
  if (leave.from === leave.to) return leave.leaveHalfSession ? 0.5 : 1;
  if (dateStr === leave.from && leave.leaveHalfFirst) return 0.5;
  if (dateStr === leave.to && leave.leaveHalfLast) return 0.5;
  return 1;
};

/** Returns true if the dateStr (YYYY-MM-DD) is a Sunday (weekly off). */
const isSunday = (dateStr) => {
  const d = parseDateKey(dateStr);
  return d ? d.getDay() === 0 : false;
};

/** Calculate total approved leave days for a leave entry, excluding Sundays. */
const calcApprovedLeaveDays = (leave) => {
  if (leave.status !== 'approved') return 0;
  const start = parseDateKey(leave.from);
  const end = parseDateKey(leave.to);
  if (!start || !end || start > end) return 0;
  // Single day — skip if Sunday
  if (leave.from === leave.to) {
    if (isSunday(leave.from)) return 0;
    return leave.leaveHalfSession ? 0.5 : 1;
  }
  let total = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const dateKey = formatDateKey(cursor);
    if (!isSunday(dateKey)) {
      total += getLeaveDayFraction(leave, dateKey);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
};

const buildLeaveMaps = (employeeLeaves, monthDateKeys) => {
  const monthKeySet = new Set(monthDateKeys);
  const allByDate = {};
  const activeByDate = {};

  (employeeLeaves || []).forEach((leave) => {
    const start = parseDateKey(leave.from);
    const end = parseDateKey(leave.to);
    if (!start || !end || start > end) return;

    const cursor = new Date(start);
    while (cursor <= end) {
      const dateKey = formatDateKey(cursor);
      // Skip Sundays — weekly off, don't count as leave day
      if (!isSunday(dateKey) && monthKeySet.has(dateKey)) {
        const enriched = { ...leave, fraction: getLeaveDayFraction(leave, dateKey) };
        allByDate[dateKey] = [...(allByDate[dateKey] || []), enriched];
        if (isActiveLeaveStatus(leave.status)) {
          activeByDate[dateKey] = [...(activeByDate[dateKey] || []), enriched];
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  return { allByDate, activeByDate };
};

export default function EmployeeAttendanceSummary({
  employees = [],
  leaves = [],
  initialMonthlyData = [],
  initialMonthStart = '',
  initialMonthEnd = '',
  showToast,
}) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filters, setFilters] = useState({ department: '', employee: '' });
  const [monthlyAttendanceData, setMonthlyAttendanceData] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const bounds = getMonthBounds(today);
    const fromMemory = bounds.monthStart === initialMonthStart && bounds.monthEnd === initialMonthEnd
      ? initialMonthlyData
      : [];
    return fromMemory.length
      ? fromMemory
      : (readHrMonthlyAttendanceCache(bounds.monthStart, bounds.monthEnd) || []);
  });
  const [monthlyLoading, setMonthlyLoading] = useState(() => monthlyAttendanceData.length === 0);
  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');

  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const selectedMonth = useMemo(() => selectedDate.slice(5, 7), [selectedDate]);
  const selectedYear = useMemo(() => selectedDate.slice(0, 4), [selectedDate]);
  const { monthStart, monthEnd, monthLabel } = useMemo(
    () => getMonthBounds(selectedDate),
    [selectedDate]
  );
  const monthSummaryEnd = useMemo(() => {
    const selectedMonthValue = getMonthValue(monthStart);
    const todayMonthValue = getMonthValue(todayStr);
    if (selectedMonthValue > todayMonthValue) return null;
    if (selectedMonthValue === todayMonthValue) return todayStr;
    return monthEnd;
  }, [monthEnd, monthStart, todayStr]);
  const monthDateKeys = useMemo(
    () => (monthSummaryEnd ? getMonthDateKeys(monthStart, monthSummaryEnd) : []),
    [monthStart, monthSummaryEnd]
  );

  useEffect(() => {
    let active = true;
    const cachedRows = readHrMonthlyAttendanceCache(monthStart, monthEnd);
    const canUseInitial =
      monthStart === initialMonthStart &&
      monthEnd === initialMonthEnd &&
      initialMonthlyData.length > 0;

    if (cachedRows?.length) {
      setMonthlyAttendanceData(cachedRows);
      setMonthlyLoading(false);
    } else if (canUseInitial) {
      setMonthlyAttendanceData(initialMonthlyData);
      setMonthlyLoading(false);
      writeHrMonthlyAttendanceCache(monthStart, monthEnd, initialMonthlyData);
    } else {
      setMonthlyLoading(true);
    }

    fetchAttendanceRowsForRangeFast(monthStart, monthEnd)
      .then((rows) => {
        if (!active) return;
        setMonthlyAttendanceData(rows);
        setMonthlyLoading(false);
        writeHrMonthlyAttendanceCache(monthStart, monthEnd, rows);
      })
      .catch((error) => {
        console.error('Employee attendance summary fetch error:', error);
        if (!active) return;
        setMonthlyLoading(false);
        if (!(cachedRows?.length || canUseInitial)) {
          showToast('Failed to load employee attendance summary', 'err');
        }
      });

    return () => {
      active = false;
    };
  }, [initialMonthEnd, initialMonthStart, initialMonthlyData, monthEnd, monthStart, showToast]);

  const departments = useMemo(
    () => [...new Set(employees.map((employee) => employee.department || employee.dept).filter(Boolean))],
    [employees]
  );
  const yearOptions = useMemo(() => {
    const years = new Set([Number(selectedYear), new Date().getFullYear()]);
    monthlyAttendanceData.forEach((row) => {
      if (row?.date) years.add(Number(row.date.slice(0, 4)));
    });
    leaves.forEach((leave) => {
      if (leave?.from) years.add(Number(leave.from.slice(0, 4)));
      if (leave?.to) years.add(Number(leave.to.slice(0, 4)));
    });
    for (let year = new Date().getFullYear() - 2; year <= new Date().getFullYear() + 2; year += 1) {
      years.add(year);
    }
    return [...years].filter(Number.isFinite).sort((a, b) => b - a);
  }, [leaves, monthlyAttendanceData, selectedYear]);

  const monthlyEmployeeSummaries = useMemo(() => {
    const employeeMap = new Map();

    employees.forEach((employee) => {
      const email = employee.email || employee.id;
      if (!email) return;
      employeeMap.set(email, {
        email,
        name: employee.name || email.split('@')[0],
        department: employee.department || employee.dept || 'N/A',
      });
    });

    monthlyAttendanceData.forEach((row) => {
      const email = row.email || row.userId;
      if (!email) return;
      if (!employeeMap.has(email)) {
        employeeMap.set(email, {
          email,
          name: row.userName || email.split('@')[0],
          department: row.department || 'N/A',
        });
      }
    });

    return [...employeeMap.values()].map((employee) => {
      const employeeRows = monthlyAttendanceData.filter((row) => {
        const rowEmail = row.email || row.userId;
        return rowEmail === employee.email;
      });
      const attendanceByDate = new Map(
        employeeRows.filter((row) => row.date).map((row) => [row.date, row])
      );
      const employeeLeaves = leaves.filter(
        (leave) => leave.eEmail === employee.email || leave.emp === employee.name
      );
      const { allByDate, activeByDate } = buildLeaveMaps(employeeLeaves, monthDateKeys);

      let presentDays = 0;
      let absentDays = 0;
      let halfDays = 0;

      monthDateKeys.forEach((dateKey) => {
        const record = attendanceByDate.get(dateKey);
        const hasAttendance = !!(record && (record.checkIn || PRESENT_STATUSES.has(record.status)));

        // Sunday = weekly off by default, but if employee actually checked in → count as present
        if (isSunday(dateKey) && !hasAttendance) return;

        if (hasAttendance) {
          presentDays += 1;
          if (isHalfDayRecord(record)) halfDays += 1;
          return;
        }
        if ((activeByDate[dateKey] || []).length > 0) return;
        absentDays += 1;
      });

      // leaveDays = approved leaves in the selected month
      const leaveDays = Object.values(activeByDate).reduce((sum, entries) => {
        const fraction = entries.reduce(
          (max, leaveEntry) => Math.max(max, leaveEntry.fraction || 1),
          0
        );
        return sum + fraction;
      }, 0);

      // approvedLeavesList = all approved leaves (all time) for this employee
      const approvedLeavesList = employeeLeaves
        .filter((l) => l.status === 'approved')
        .sort((a, b) => (b.from || '').localeCompare(a.from || ''));

      // approvedLeaveDays = total approved leave days across all time
      const approvedLeaveDays = approvedLeavesList.reduce(
        (sum, l) => sum + calcApprovedLeaveDays(l),
        0
      );

      const timeline = monthDateKeys.map((dateKey) => {
        const record = attendanceByDate.get(dateKey);
        const dayLeaves = allByDate[dateKey] || [];
        const activeLeaves = activeByDate[dateKey] || [];
        const isPresent = !!(record && (record.checkIn || PRESENT_STATUSES.has(record.status)));
        // isSunday flag only applies when no attendance — if they checked in, treat as normal workday
        const weeklyOff = isSunday(dateKey) && !isPresent;

        return {
          date: dateKey,
          record: record || null,
          leaves: dayLeaves,
          halfDay: isPresent ? isHalfDayRecord(record) : false,
          isSunday: weeklyOff,
          statusLabel: weeklyOff
            ? 'Weekly Off'
            : isPresent
              ? record.status === 'IN'
                ? 'Working'
                : 'Present'
              : activeLeaves.length > 0
                ? 'Leave'
                : 'Absent',
        };
      });

      return {
        ...employee,
        presentDays,
        absentDays,
        halfDays,
        leaveDays,
        approvedLeaveDays,
        approvedLeavesList,
        timeline,
      };
    }).sort((a, b) => {
      if (b.presentDays !== a.presentDays) return b.presentDays - a.presentDays;
      if (a.absentDays !== b.absentDays) return a.absentDays - b.absentDays;
      return a.name.localeCompare(b.name);
    });
  }, [employees, leaves, monthDateKeys, monthlyAttendanceData]);

  const filteredMonthlySummaries = useMemo(() => {
    return monthlyEmployeeSummaries.filter((employee) => {
      const matchesDepartment = !filters.department || employee.department === filters.department;
      const search = filters.employee.toLowerCase();
      const matchesEmployee = !search ||
        employee.name?.toLowerCase().includes(search) ||
        employee.email?.toLowerCase().includes(search);
      return matchesDepartment && matchesEmployee;
    });
  }, [filters.department, filters.employee, monthlyEmployeeSummaries]);

  useEffect(() => {
    if (selectedEmployeeEmail && !filteredMonthlySummaries.some((employee) => employee.email === selectedEmployeeEmail)) {
      setSelectedEmployeeEmail('');
    }
  }, [filteredMonthlySummaries, selectedEmployeeEmail]);

  const renderEmployeeDetail = (employeeSummary) => (
    <div style={{ padding: '18px 20px', background: '#fcfdff' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>{employeeSummary.name} - {monthLabel}</h3>
        <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: '6px' }}>
          {employeeSummary.email} {employeeSummary.department ? `· ${employeeSummary.department}` : ''}
        </div>
      </div>

      {/* Monthly Stats */}
      <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
        {monthLabel} Summary
      </div>
      <div className="attendance-month-detail-grid" style={{ marginBottom: '20px' }}>
        <div className="attendance-month-stat">
          <div className="attendance-month-stat-label">Present Days</div>
          <div className="attendance-month-stat-value" style={{ color: '#2a9d4f' }}>{employeeSummary.presentDays}</div>
        </div>
        <div className="attendance-month-stat">
          <div className="attendance-month-stat-label">Absent Days</div>
          <div className="attendance-month-stat-value" style={{ color: '#c0392b' }}>{employeeSummary.absentDays}</div>
        </div>
        <div className="attendance-month-stat">
          <div className="attendance-month-stat-label">Half Days</div>
          <div className="attendance-month-stat-value" style={{ color: '#d97706' }}>{employeeSummary.halfDays}</div>
        </div>
        <div className="attendance-month-stat">
          <div className="attendance-month-stat-label">Leave Days (This Month)</div>
          <div className="attendance-month-stat-value" style={{ color: '#3b5bdb' }}>{employeeSummary.leaveDays || 0}</div>
        </div>
      </div>

      {/* Total Approved Leaves (All Time) */}
      <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
        Total Approved Leaves (All Time)
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '20px',
        background: '#edfaf2', border: '1px solid #b7ebd1', borderRadius: '12px',
        padding: '14px 18px', marginBottom: '20px', flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '80px' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#2a9d4f', lineHeight: 1 }}>
            {employeeSummary.approvedLeaveDays % 1 === 0
              ? employeeSummary.approvedLeaveDays
              : employeeSummary.approvedLeaveDays.toFixed(1)}
          </div>
          <div style={{ fontSize: '11px', color: '#2a9d4f', fontWeight: '600', marginTop: '4px' }}>Total Days</div>
        </div>
        <div style={{ flex: 1, minWidth: '160px' }}>
          {employeeSummary.approvedLeavesList.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--text2)' }}>No approved leaves found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {employeeSummary.approvedLeavesList.map((lv, idx) => {
                const days = calcApprovedLeaveDays(lv);
                return (
                  <div key={lv.id || idx} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    fontSize: '13px'
                  }}>
                    <span style={{
                      background: '#edf7ff', color: '#1e7ec8',
                      borderRadius: '8px', padding: '2px 8px',
                      fontWeight: '600', fontSize: '11px', whiteSpace: 'nowrap'
                    }}>
                      {lv.type || 'Leave'}
                    </span>
                    <span style={{ color: 'var(--text)', fontWeight: '500' }}>
                      {lv.from}{lv.from !== lv.to ? ` → ${lv.to}` : ''}
                    </span>
                    <span style={{
                      marginLeft: 'auto', background: '#edfaf2', color: '#2a9d4f',
                      borderRadius: '999px', padding: '2px 10px',
                      fontWeight: '700', fontSize: '11px', whiteSpace: 'nowrap'
                    }}>
                      {days % 1 === 0 ? days : days.toFixed(1)} day{days !== 1 ? 's' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid var(--border)', borderRadius: '12px', background: '#fff' }}>
        <table className="attendance-timeline-table">
          <thead>
            <tr style={{ background: 'var(--bg-sec)' }}>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>DATE</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>CHECK IN</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>CHECK OUT</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>HOURS</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>STATUS</th>
              <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>LEAVE</th>
            </tr>
          </thead>
          <tbody>
            {employeeSummary.timeline.map((day) => (
              <tr key={day.date} style={{
                borderTop: '1px solid var(--border)',
                background: day.isSunday ? '#f8f8f8' : 'transparent',
                opacity: day.isSunday ? 0.65 : 1,
              }}>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: day.isSunday ? 'var(--text2)' : 'inherit' }}>{formatDayLabel(day.date)}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{day.isSunday ? '--' : formatTimeValue(day.record?.checkIn)}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                  {day.isSunday ? '--' : day.record?.status === 'IN' && !day.record?.checkOut ? (
                    <span style={{ color: '#d97706' }}>In Progress...</span>
                  ) : (
                    formatTimeValue(day.record?.checkOut)
                  )}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600' }}>
                  {day.isSunday ? '--' : (day.record?.totalHours > 0 ? formatDuration(day.record.totalHours) : '--')}
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: day.isSunday
                        ? '#f1f1f1'
                        : day.record
                          ? (day.record.status === 'IN' ? '#fffbeb' : day.halfDay ? '#fff7ed' : '#eef2ff')
                          : (day.statusLabel === 'Leave' ? '#eef2ff' : '#fee2e2'),
                      color: day.isSunday
                        ? '#999'
                        : day.record
                          ? (day.record.status === 'IN' ? '#d97706' : day.halfDay ? '#c2410c' : '#3b5bdb')
                          : (day.statusLabel === 'Leave' ? '#3b5bdb' : '#dc2626'),
                    }}>
                      {day.isSunday
                        ? 'WEEKLY OFF'
                        : day.record
                          ? (day.record.status === 'IN'
                            ? 'WORKING'
                            : day.halfDay
                              ? 'HALF DAY'
                              : 'PRESENT')
                          : day.statusLabel.toUpperCase()}
                    </span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text2)' }}>
                  {day.isSunday || day.leaves.length === 0 ? '--' : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {day.leaves.map((leaveEntry) => (
                        <div key={`${leaveEntry.id || leaveEntry.from}-${leaveEntry.status}-${day.date}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '600', color: 'var(--text)' }}>{leaveEntry.type || 'Leave'}</span>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '999px',
                            fontSize: '10px',
                            fontWeight: '700',
                            background: leaveEntry.status === 'approved' ? '#edfaf2' : leaveEntry.status === 'rejected' ? '#fef2f2' : '#fffbeb',
                            color: leaveEntry.status === 'approved' ? '#2a9d4f' : leaveEntry.status === 'rejected' ? '#c0392b' : '#b45309'
                          }}>
                            {normalizeLeaveStatus(leaveEntry.status)}
                          </span>
                          <span>{leaveEntry.fraction === 0.5 ? 'Half day' : 'Full day'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="pg">
      <div className="ph">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileSpreadsheet size={24} /> Employee Attendance Summary
          </h1>
          <p>Monthly employee-wise present, absent, half day, and leave details</p>
        </div>
      </div>

      <div className="card attendance-filters-card" style={{ marginBottom: '20px', padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <Filter size={18} color="var(--text2)" />
          <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text)' }}>Filters</span>
        </div>

        <div className="attendance-filter-grid">
          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Month</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={16} color="var(--text2)" style={{ flexShrink: 0 }} />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedDate(`${selectedYear}-${e.target.value}-01`)}
                className="attendance-filter-input"
              >
                {MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          </label>

          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Year</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedDate(`${e.target.value}-${selectedMonth}-01`)}
              className="attendance-filter-input"
            >
              {yearOptions.map((year) => (
                <option key={year} value={String(year)}>{year}</option>
              ))}
            </select>
          </label>

          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Department</span>
            <select
              value={filters.department}
              onChange={(e) => setFilters((prev) => ({ ...prev, department: e.target.value }))}
              className="attendance-filter-input"
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </label>

          <label className="attendance-filter-field">
            <span className="attendance-filter-label">Search employee</span>
            <div style={{ position: 'relative', width: '100%' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text3)',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder="Name or email..."
                value={filters.employee}
                onChange={(e) => setFilters((prev) => ({ ...prev, employee: e.target.value }))}
                className="attendance-filter-input"
                style={{ paddingLeft: '36px' }}
              />
            </div>
          </label>
        </div>
      </div>

      <style>{`
        .attendance-filter-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: var(--text2);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .attendance-filter-input {
          width: 100%;
          box-sizing: border-box;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 13px;
          background: var(--surface, #fff);
          color: var(--text);
          min-height: 38px;
        }
        .attendance-filter-input:focus {
          outline: none;
          border-color: var(--accent, #3b5bdb);
          box-shadow: 0 0 0 2px rgba(59, 91, 219, 0.12);
        }
        .attendance-filter-field {
          display: flex;
          flex-direction: column;
          margin: 0;
          min-width: 0;
        }
        .attendance-filter-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px 16px;
          align-items: end;
        }
        .attendance-month-row:hover {
          background: #fafbff;
        }
        .attendance-month-detail-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        .attendance-month-stat {
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 14px;
          background: var(--surface, #fff);
        }
        .attendance-month-stat-label {
          font-size: 11px;
          font-weight: 700;
          color: var(--text2);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 6px;
        }
        .attendance-month-stat-value {
          font-size: 22px;
          font-weight: 700;
          color: var(--text);
        }
        .attendance-timeline-table {
          min-width: 900px;
          width: 100%;
          border-collapse: collapse;
        }
        @media (max-width: 900px) {
          .attendance-filter-grid {
            grid-template-columns: 1fr;
          }
          .attendance-month-detail-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 640px) {
          .attendance-month-detail-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="card" style={{ padding: '0' }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileSpreadsheet size={18} />
              Monthly Employee Summary
            </h3>
            <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '4px' }}>
              {monthLabel} ke liye present, absent, half day aur leave day summary
              {monthSummaryEnd === todayStr ? ' (till today)' : ''}
            </div>
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
            {filteredMonthlySummaries.length} employees
          </span>
        </div>

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ minWidth: '920px', width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-sec)' }}>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>EMPLOYEE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'left' }}>DEPARTMENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>PRESENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>ABSENT</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>HALF DAY</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>LEAVE (Month)</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'center' }}>APPROVED (Total)</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)', fontWeight: '500', textAlign: 'right' }}>DETAIL</th>
              </tr>
            </thead>
            <tbody>
              {monthlyLoading && filteredMonthlySummaries.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: 'var(--text2)' }}>
                    <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: '10px' }} />
                    <div>Loading employee summary...</div>
                  </td>
                </tr>
              ) : filteredMonthlySummaries.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: 'var(--text2)' }}>
                    No monthly employee summary found for this filter.
                  </td>
                </tr>
              ) : (
                filteredMonthlySummaries.map((employee, index) => {
                  const isOpen = selectedEmployeeEmail === employee.email;
                  return (
                    <React.Fragment key={employee.email}>
                      <tr
                        className="attendance-month-row"
                        style={{
                          borderBottom: isOpen ? 'none' : '1px solid var(--border)',
                          cursor: 'pointer',
                          background: isOpen ? '#f8faff' : 'transparent',
                        }}
                        onClick={() => setSelectedEmployeeEmail(isOpen ? '' : employee.email)}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div
                              className="av"
                              style={{
                                background: ecb(index),
                                color: ec(index),
                                width: '32px',
                                height: '32px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                fontWeight: '600',
                              }}
                            >
                              {employee.name?.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>{employee.name}</div>
                              <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{employee.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>{employee.department || 'N/A'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#2a9d4f' }}>{employee.presentDays}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#c0392b' }}>{employee.absentDays}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#d97706' }}>{employee.halfDays}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600', color: '#3b5bdb' }}>{employee.leaveDays || 0}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', textAlign: 'center' }}>
                          {employee.approvedLeaveDays > 0 ? (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              background: '#edfaf2', color: '#2a9d4f',
                              borderRadius: '10px', padding: '4px 10px',
                              fontWeight: '700', fontSize: '12px'
                            }}>
                              ✓ {employee.approvedLeaveDays % 1 === 0 ? employee.approvedLeaveDays : employee.approvedLeaveDays.toFixed(1)} days
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text3)', fontSize: '12px' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--p)', fontSize: '12px', fontWeight: '600' }}>
                            {isOpen ? 'Hide details' : 'View details'}
                            <ChevronRight size={14} style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <td colSpan="8" style={{ padding: 0 }}>
                            {renderEmployeeDetail(employee)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
